import { useLayoutEffect, useRef, useState } from 'react';
import type { CeoBankPayment } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { formatDateRu, formatMoney, PAYMENT_CATEGORY_LABELS } from '../ceoFormat';
import { accountTail, categoryShortLabel, paymentSourceLabel } from './paymentView';
import {
  AccountMonoText,
  AmountCell,
  CellBadge,
  CounterpartyInnText,
  CounterpartyNameText,
  DocDateText,
  DocNumberText,
  MoreButton,
  PaymentsTable,
  PurposeCell,
  PurposeClamp,
  SourceText,
  TableNoticeCell,
  TableWrap,
} from './styles';

interface DailyPaymentsTableProps {
  /** `undefined` — операции выбранного дня ещё загружаются. */
  payments: CeoBankPayment[] | undefined;
}

/** Назначение платежа: две строки, полный текст — по кнопке «ещё» (мышью и с клавиатуры). */
function PaymentPurpose({ text }: { text: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [clipped, setClipped] = useState(false);

  // Кнопка нужна, только если текст не поместился в две строки, — и на любой ширине колонки.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || expanded) return;
    const check = () => setClipped(el.scrollHeight - el.clientHeight > 1);
    check();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, expanded]);

  return (
    <>
      <PurposeClamp ref={ref} $expanded={expanded}>
        {text}
      </PurposeClamp>
      {(clipped || expanded) && (
        <MoreButton type="button" aria-expanded={expanded} onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'свернуть' : 'ещё'}
        </MoreButton>
      )}
    </>
  );
}

export function DailyPaymentsTable({ payments }: DailyPaymentsTableProps) {
  return (
    <TableWrap>
      <PaymentsTable>
        <thead>
          <tr>
            <th>Дата / Документ</th>
            <th>Банковский счёт</th>
            <th>Контрагент</th>
            <th>Назначение платежа</th>
            <th>Категория ДДС</th>
            <th>Сумма</th>
            <th>Источник</th>
          </tr>
        </thead>
        <tbody>
          {payments === undefined ? (
            <tr>
              <TableNoticeCell colSpan={7}>Загрузка операций…</TableNoticeCell>
            </tr>
          ) : payments.length === 0 ? (
            <tr>
              <TableNoticeCell colSpan={7}>Операций за этот день нет</TableNoticeCell>
            </tr>
          ) : (
            payments.map((p) => {
              const category = PAYMENT_CATEGORY_LABELS[p.category] ?? p.category;
              return (
                <tr key={p.id}>
                  <td>
                    <DocDateText>{formatDateRu(p.payment_date)}</DocDateText>
                    <DocNumberText>{p.document_number ?? '—'}</DocNumberText>
                  </td>
                  <td>
                    <div>{p.bank_name ?? 'Счёт компании'}</div>
                    {p.account_number && (
                      <AccountMonoText title={`Счёт ${p.account_number}`}>
                        {accountTail(p.account_number)}
                      </AccountMonoText>
                    )}
                  </td>
                  <td>
                    <CounterpartyNameText>{p.counterparty_name}</CounterpartyNameText>
                    {p.counterparty_inn && (
                      <CounterpartyInnText>ИНН {p.counterparty_inn}</CounterpartyInnText>
                    )}
                  </td>
                  <PurposeCell>
                    <PaymentPurpose text={p.purpose} />
                    {p.ks3_reference && <Badge $tone="info">{p.ks3_reference}</Badge>}
                  </PurposeCell>
                  <td>
                    <CellBadge
                      title={category}
                      $tone={
                        p.category === 'internal_transfer'
                          ? 'info'
                          : p.direction === 'inflow'
                            ? 'success'
                            : 'neutral'
                      }
                    >
                      {categoryShortLabel(p.category) ?? category}
                    </CellBadge>
                  </td>
                  <AmountCell $direction={p.direction}>
                    {p.direction === 'inflow' ? '+' : '−'}
                    {formatMoney(p.amount)}
                  </AmountCell>
                  <td>
                    {p.source === 'manual' ? (
                      <CellBadge
                        $tone="warning"
                        title="Внесена вручную: меняет учётный остаток в ОСНОВЕ, банком не подтверждена"
                      >
                        Вручную
                      </CellBadge>
                    ) : (
                      <SourceText>{paymentSourceLabel(p.source)}</SourceText>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </PaymentsTable>
    </TableWrap>
  );
}
